import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function SavingsGrowthCalculator() {
  const { t } = useTranslation();
  const [initial, setInitial] = useState("");
  const [monthly, setMonthly] = useState("");
  const [rate, setRate] = useState("");
  const [years, setYears] = useState("");
  const [results, setResults] = useState<any>(null);

  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: t('nav.home'), url: '/' },
      { name: t('categories.finance'), url: '/finance' },
      { name: "Savings Growth Calculator", url: '/calculator/savings-growth' }
    ], lang),
    (lang) => calculatorToolSchema(
      "Savings Growth Calculator",
      "Calculate future savings with compound interest. Enter initial deposit, monthly contributions, interest rate, and time period to see total growth.",
      "https://calclab.app/calculator/savings-growth",
      lang
    )
  ]);

  const calculate = () => {
    const p = parseFloat(initial);
    const m = parseFloat(monthly);
    const r = parseFloat(rate) / 100 / 12;
    const n = parseFloat(years) * 12;
    
    if (p < 0 || m < 0 || !r || !n || n <= 0) return;
    
    const futureValueInitial = p * Math.pow(1 + r, n);
    const futureValueMonthly = m * ((Math.pow(1 + r, n) - 1) / r);
    const total = futureValueInitial + futureValueMonthly;
    const deposited = p + (m * n);
    const interest = total - deposited;
    
    setResults({
      total: total.toFixed(2),
      deposited: deposited.toFixed(2),
      interest: interest.toFixed(2)
    });
  };

  return (
    <PageLayout calculatorName="Savings Growth Calculator" calculatorPath="/calculator/savings-growth">
      <div className="grid gap-8 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Calculate Savings Growth</CardTitle>
            <CardDescription>With compound interest</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Initial Deposit ($)</Label>
              <Input type="number" value={initial} onChange={(e) => setInitial(e.target.value)} placeholder="1000" />
            </div>
            <div>
              <Label>Monthly Contribution ($)</Label>
              <Input type="number" value={monthly} onChange={(e) => setMonthly(e.target.value)} placeholder="100" />
            </div>
            <div>
              <Label>Annual Interest Rate (%)</Label>
              <Input type="number" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="5" step="0.01" />
            </div>
            <div>
              <Label>Time Period (years)</Label>
              <Input type="number" value={years} onChange={(e) => setYears(e.target.value)} placeholder="10" />
            </div>
            <Button onClick={calculate} className="w-full">Calculate</Button>
          </CardContent>
        </Card>

        {results && (
          <Card>
            <CardHeader>
              <CardTitle>Future Value</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center mb-4">
                <div className="text-5xl font-bold text-primary mb-2">${results.total}</div>
                <div className="text-lg text-muted-foreground">Total Savings</div>
              </div>
              <div className="space-y-2 border-t pt-4">
                <div className="flex justify-between text-lg">
                  <span>Total Deposited:</span>
                  <span className="font-semibold">${results.deposited}</span>
                </div>
                <div className="flex justify-between text-lg">
                  <span>Interest Earned:</span>
                  <span className="font-semibold text-green-500">${results.interest}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </PageLayout>
  );
}
